import type { CaptureResult } from 'posthog-js';

// Registrable domains of the third-party marketing scripts the storefront loads
// through CustomPixels. `capture_exceptions` reports every unhandled rejection,
// so a vendor outage (for example Klaviyo's onsite atlas bundle returning 500s)
// becomes a first-party issue. We match on the script host, not the issue,
// because minified vendor bundles shift stack columns between builds and split
// one throw site into many column-level fingerprints.
export const THIRD_PARTY_SCRIPT_DOMAINS = [
	'klaviyo.com',
	'vibe.co',
	'snapchat.com',
	'sc-static.net',
	'tiktok.com',
	'facebook.net',
	'googletagmanager.com',
	'doubleclick.net',
	'googleadservices.com',
	'postscript.io',
	'triplewhale.com',
];

function isThirdPartyScriptHost(hostname: string): boolean {
	return THIRD_PARTY_SCRIPT_DOMAINS.some(
		(domain) => hostname === domain || hostname.endsWith(`.${domain}`),
	);
}

function frameHostname(filename: unknown): string | null {
	if (typeof filename !== 'string') return null;
	try {
		return new URL(filename).hostname;
	} catch {
		return null;
	}
}

// Drops an exception event unless it keeps at least one first-party frame.
// A first-party frame has a resolvable URL that is not a vendor script host.
// This removes two noise classes:
//   - vendor-only throws, where every resolvable frame is a vendor script;
//   - frameless throws, where no frame has a resolvable URL at all. A
//     cross-origin script reports these with a type, a value, and a mechanism
//     but no stack, so each new minified value ("s.id", "o.id", "Script
//     error.") mints a fake first-party issue nobody can act on.
export function dropThirdPartyScriptExceptions(event: CaptureResult | null): CaptureResult | null {
	if (event?.event !== '$exception') return event;

	const exceptions = event.properties?.$exception_list;
	if (!Array.isArray(exceptions)) return event;

	for (const exception of exceptions) {
		const frames = exception?.stacktrace?.frames;
		if (!Array.isArray(frames)) continue;

		for (const frame of frames) {
			const hostname = frameHostname(frame?.filename);
			if (hostname && !isThirdPartyScriptHost(hostname)) return event;
		}
	}

	return null;
}
